import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardBody, CardHeader, Col, Row, Button, CardFooter } from 'reactstrap';
import RowItem from 'components/SortableRow/RowItem';
import { v4 as uuidv4 } from 'uuid';
import useConfirmation from 'hooks/useConfirmation';
import { reSortingData } from 'helpers/global';
import ItemList from './items/List';
import ItemCard from './items/Card';

const FieldRanking = ({
  onChange,
  value,
  questionRoutes,
  questionRoutesLoading,
  error,
  disabled
}) => {
  const [answers, setAnswers] = useState([]);

  useEffect(() => {
    setAnswers(value || []);
  }, [value]);

  const { showConfirmation, hideConfirmation } = useConfirmation();

  const insertAnswer = () => {
    const newAnswers = [...answers, { id: uuidv4(), answer: '', email: '', route: '' }];
    setAnswers(newAnswers);
    onChange(newAnswers);
  };

  const moveCard = useCallback((dragIndex, hoverIndex) => {
    setAnswers((localAnswers) => reSortingData(localAnswers, dragIndex, hoverIndex));
  }, []);

  const onDrop = useCallback(() => {
    onChange(answers);
  }, [answers, onChange]);

  const onChangeAnswer = useCallback(
    (index, field, value) => {
      const newAnswers = [...answers];
      newAnswers[index][field] = value;

      setAnswers(newAnswers);
      onChange(newAnswers);
    },
    [answers, setAnswers, onChange]
  );

  const renderItem = useCallback(
    (answer, index) => {
      const removeItem = (index) => {
        const newAnswers = [...answers];
        newAnswers.splice(index, 1);

        setAnswers(newAnswers);
        onChange(newAnswers);

        hideConfirmation();
      };

      const clickSort = (currentIndex, nextIndex) => {
        const newAnswers = reSortingData(answers, currentIndex, nextIndex);
        setAnswers(newAnswers);
        onChange(newAnswers);
      };

      return (
        <RowItem id={answer.id} key={answer.id} index={index} moveCard={moveCard} onDrop={onDrop}>
          {({ dragRef, previewRef, handlerId, isDragging }) => (
            <>
              <ItemCard
                index={index}
                isFirst={index <= 0}
                isLast={index >= answers.length - 1}
                clickSort={clickSort}
                answer={answer}
                showConfirmation={showConfirmation}
                removeItem={removeItem}
                error={error}
                onChangeAnswer={onChangeAnswer}
                questionRoutesLoading={questionRoutesLoading}
                questionRoutes={questionRoutes}
                className="d-md-none"
                disabled={disabled}
              />

              <ItemList
                index={index}
                dragRef={dragRef}
                previewRef={previewRef}
                handlerId={handlerId}
                isDragging={isDragging}
                answer={answer}
                error={error}
                onChangeAnswer={onChangeAnswer}
                questionRoutesLoading={questionRoutesLoading}
                questionRoutes={questionRoutes}
                showConfirmation={showConfirmation}
                removeItem={removeItem}
                className="d-none d-md-flex"
                disabled={disabled}
              />
            </>
          )}
        </RowItem>
      );
    },
    [
      questionRoutes,
      questionRoutesLoading,
      moveCard,
      answers,
      onChangeAnswer,
      showConfirmation,
      hideConfirmation,
      onChange,
      error,
      onDrop,
      disabled
    ]
  );

  return (
    <Row>
      <Col>
        <div className="card-wrapper">
          <Card>
            <CardHeader>
              <Row>
                <Col xs="6">
                  <h3 className="mb-0 text-brand">
                    Answers
                    {error ? (
                      <span className="d-inline-block ml-2 invalid-feedback font-weight-normal w-auto">
                        - {error}
                      </span>
                    ) : (
                      ''
                    )}
                  </h3>
                  <div className='text-xs'>Max. 10 answers</div>
                </Col>
                {!disabled && (
                  <Col className="text-right" xs="6">
                    <Button
                      className="btn-neutral mr-0"
                      size="sm"
                      color="default"
                      type="button"
                      disabled={answers.length >= 10}
                      onClick={() => insertAnswer()}>
                      Add Answer
                    </Button>
                  </Col>
                )}
              </Row>
            </CardHeader>
            <CardBody>
              {answers.length <= 0 && <p className="text-center">There is no data!</p>}
              {answers.length >= 1 && (
                answers.map((answer, index) => renderItem(answer, index))
              )}
            </CardBody>
            {answers.length >= 6 && !disabled && (
              <CardFooter>
                <Button
                  className="btn-neutral ml-auto d-block mr-0"
                  size="sm"
                  color="default"
                  type="button"
                  onClick={() => insertAnswer()}
                  disabled={answers.length >= 10}>
                  Add Answer
                </Button>
              </CardFooter>
            )}
          </Card>
        </div>
      </Col>
    </Row>
  );
};

export default FieldRanking;
